import { defineFilter } from '../engine/descriptor';
import type { FilterDefinition, FilterHandler } from '../engine/types';
import { FilterId, FilterType, type RangeFilter } from '../types/filters';

const MIN_YEARS = 0;
const MAX_YEARS = 100;

const clampYears = (
  years: number | undefined,
  min: number,
  max: number
): number | undefined => {
  if (years === undefined) {
    return undefined;
  }
  if (years < min) {
    return min;
  }
  if (years > max) {
    return max;
  }
  return years;
};

const isDefaultRange = (
  filterState: RangeFilter,
  definition: FilterDefinition<RangeFilter>
) => {
  const defaultMin = definition.min;
  const defaultMax = definition.max;

  return filterState.min === defaultMin && filterState.max === defaultMax;
};

const yearsOfExperienceHandler: FilterHandler<RangeFilter> = {
  isActive(currentFilterState: RangeFilter) {
    const { min, max } = currentFilterState;
    return min !== undefined || max !== undefined;
  },
  isDefault(currentFilterState: RangeFilter, definition: RangeFilter) {
    return isDefaultRange(currentFilterState, definition);
  },
  getEffectiveFilter(
    currentFilterState: RangeFilter,
    definition: FilterDefinition<RangeFilter>
  ) {
    const isUserSubmitted = !isDefaultRange(currentFilterState, definition);

    if (!isUserSubmitted) {
      return {
        ...currentFilterState,
        min: undefined,
        max: undefined,
        isUserSubmitted: false,
        isVisible: false,
      };
    }

    const providedMin = currentFilterState.min;
    const providedMax = currentFilterState.max;

    const finalMin = clampYears(providedMin, MIN_YEARS, MAX_YEARS);
    const finalMax = (() => {
      const max = clampYears(providedMax, MIN_YEARS, MAX_YEARS);

      if (finalMin && max && max < finalMin) {
        return finalMin;
      }

      return max;
    })();

    return {
      ...currentFilterState,
      min: finalMin,
      max: finalMax,
      isUserSubmitted: true,
      isVisible: true,
    };
  },
};

export const yearsOfExperienceDescriptor = defineFilter({
  definition: {
    id: FilterId.YEARS_OF_EXPERIENCE,
    name: 'Years of Experience',
    type: FilterType.RANGE,
    min: undefined,
    max: undefined,
    isActive: false,
    isVisible: true,
  },
  handler: yearsOfExperienceHandler,
  multi: { id: FilterId.MULTI_YEARS_OF_EXPERIENCE },
});
